"use client";

import { AccordionItem } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/accordion-item/AccordionItem";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { PlusOutlined as Plus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined as Minus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { DescriptionIcons } from "./DescriptionIcons";
import { DescriptionLinks } from "./DescriptionLinks";
import { ProductType } from "@/types/types.ts";
import styles from "./ProductDescriptionAccordion.module.scss";
import baseStyles from "../../ProductAccordion.module.scss";
import { useRef, useState } from "react";
import classNames from "classnames";
import { useFading } from "./useFading";
import { Icon, Link } from "@/types/prodcatBaseTypes.ts";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { ProductAccordionWithMonetateEvents } from "../../ProductAccordionWithMonetateEvents";

export type Props = {
  productType: ProductType;
  displayCode: string;
  description: string;
  icons: Icon[];
  links: Link[];
  endorsement:
    | {
        title: string;
        description?: string | null | undefined;
      }
    | undefined;
  open?: boolean;
  hideDescriptionLinks?: boolean;
  showBeautySocietyLink?: boolean;
};

export const PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME =
  "product-description";

export function ProductDescriptionAccordionUI({
  productType,
  displayCode,
  description,
  icons,
  links,
  endorsement,
  open,
  hideDescriptionLinks,
  showBeautySocietyLink,
}: Props) {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [contentHeight, doFade] = useFading(descriptionRef);
  const [isOpen, setIsOpen] = useState(open);
  const descriptionContainerStyle = styles.descriptionContainer;
  const shouldFadeAndTruncate = doFade && !truncated;
  const containerStyles = shouldFadeAndTruncate
    ? classNames(descriptionContainerStyle, styles.truncated)
    : classNames(descriptionContainerStyle);
  const actualHeight = shouldFadeAndTruncate ? contentHeight : "100%";

  const toggle = () => {
    setTruncated(!truncated);
  };

  if (productType === "RetiredStaticBundle") return null;

  return (
    <ProductAccordionWithMonetateEvents
      name="Product Description"
      open={isOpen}
    >
      <AccordionItem
        id="accordion-description"
        data-testid="accordion:description"
        header={
          <h2 className={baseStyles.accordionTitle}>Product description</h2>
        }
        isOpen={open}
        instanceName={PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME}
        onStateChange={(isOpen: boolean) => {
          setIsOpen(isOpen);
        }}
      >
        <div
          className={containerStyles}
          ref={descriptionRef}
          style={{ maxHeight: actualHeight }}
        >
          <p data-testid="description:code">
            <strong>Product code: {displayCode}</strong>
          </p>

          <DescriptionIcons icons={icons} />

          <div
            className={styles.descriptionContent}
            data-testid="description:content"
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
          {endorsement && (
            <div
              id="description:endorsement"
              data-testid="description:endorsement"
              className={styles.endorsement}
            >
              <h3
                className={styles.endorsementTitle}
                data-testid="description:endorsement:title"
              >
                {endorsement.title}
              </h3>
              {endorsement.description && (
                <div
                  data-testid="description:endorsement:description"
                  id="description:endorsement:description"
                  dangerouslySetInnerHTML={{ __html: endorsement.description }}
                />
              )}
            </div>
          )}

          {!hideDescriptionLinks && <DescriptionLinks links={links} />}

          {showBeautySocietyLink && (
            <div
              className={styles.beautySocityLink}
              data-testid="beauty-society:link"
            >
              <UnderlinedButton
                tag="a"
                target="_self"
                href={"https://www.johnlewis.com/our-services/beauty-society"}
              >
                Read more about beauty consultations
              </UnderlinedButton>
            </div>
          )}
        </div>
        {doFade && (
          <TertiaryButton
            onClick={toggle}
            IconComponent={
              truncated ? Minus24PxOutlinedIcon : Plus24PxOutlinedIcon
            }
            iconPositionRight
            aria-expanded={truncated}
            aria-controls="product-description-toggle"
            data-testid="product:description:showmoreless"
            small={true}
          >
            <span>{!truncated ? "Show more" : "Show less"}</span>
          </TertiaryButton>
        )}
      </AccordionItem>
    </ProductAccordionWithMonetateEvents>
  );
}
